import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';

import {
  listBlueprints,
  launchBlueprint,
  selectors,
  ADMIN,
  TRAINER,
  BLUEPRINT_PUBLISHED,
  Blueprint,
  BlueprintVersion,
  BlueprintVariant,
  LaunchSummary,
} from 'state';
import {
  LoadFailure,
  AnswerKeyGateTag,
  INCOMPLETE_COVERAGE_HEADLINE,
  VerdictBanner,
  gradedFraction,
  notGradedLine,
  readAnswerKeyGate,
  readCoverage,
} from 'components';
import useBlueprintPreparation from './useBlueprintPreparation';

type Props = {
  klassId: number;
  visible: boolean;
  hide: () => void;
};

/**
 * The card is `LaunchSummary` (state/app/blueprints/types), which the variant
 * now declares, so this screen no longer casts its way to it.
 */
const launchSummaryOf = (
  variant?: BlueprintVariant,
  version?: BlueprintVersion,
): LaunchSummary | undefined => {
  const fallback = version as unknown as
    | { launch_summary?: LaunchSummary }
    | undefined;
  return variant?.launch_summary || fallback?.launch_summary;
};

/** The latest PUBLISHED version of a family (or undefined if none). */
const latestPublishedVersion = (
  blueprint: Blueprint,
): BlueprintVersion | undefined =>
  [...blueprint.versions]
    .filter(v => v.status === BLUEPRINT_PUBLISHED)
    .sort((a, b) => b.version_number - a.version_number)[0];

/**
 * Engineering fixtures that were published into the same table as real
 * scenarios and are not something to run a class on.
 *
 * This is a named list, not a "contains the word test" rule, because a real
 * scenario may legitimately be called a stress test and hiding it would be
 * worse than showing a fixture. It is a stopgap: the durable fix is a flag on
 * the blueprint itself, so a fixture never has to be recognised by its name.
 */
const ENGINEERING_FIXTURE_NAMES = new Set([
  'predetermined dates test',
  'dgc_bh_hist',
]);

const normalise = (name: string) =>
  name.trim().toLowerCase().replace(/\s+/g, ' ');

/** A name with no spaces and no lower case is a machine code, not a scenario
 *  title ("DGC_BH_NY_HIST_12JUN2026"). */
const looksLikeAMachineCode = (name: string) =>
  !/\s/.test(name.trim()) && name === name.toUpperCase() && /[_\d]/.test(name);

const isEngineeringFixture = (blueprint: Blueprint) => {
  const name = normalise(blueprint.name || '');
  if (!name) return true;
  if (ENGINEERING_FIXTURE_NAMES.has(name)) return true;
  return looksLikeAMachineCode(blueprint.name || '');
};

/**
 * A scenario window is a range of CALENDAR DAYS, and the endpoint sends each
 * end of it as a bare `YYYY-MM-DD`. `new Date('2022-09-01')` reads that as
 * midnight UTC, which west of Greenwich renders as 31 August -- a trainer told
 * the wrong start day for the sitting they are about to book. Build the day in
 * the reader's own calendar instead.
 */
const formatDate = (value?: string | null) => {
  if (!value) return '';
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const windowLabel = (summary?: LaunchSummary) => {
  const from = formatDate(summary?.start_date);
  const to = formatDate(summary?.end_date);
  if (from && to) return `${from} — ${to}`;
  return from || to || '';
};

/**
 * Launch from Scenario (trainer) — pick a scenario, read what it will put in
 * front of the class, choose a difficulty, and launch into the selected class.
 * The new session arrives via the klass socket, exactly like createSession.
 *
 * Trainers see the scenario's dates and instruments. Assessment maintenance
 * and scoring diagnostics belong to administrators.
 */
const LaunchScenarioModal: React.FC<Props> = ({ klassId, visible, hide }) => {
  const dispatch = useDispatch();
  const blueprints = useSelector(selectors.blueprintsList);
  const waiting = useSelector(selectors.blueprintsWaiting);
  const catalogueError = useSelector(selectors.blueprintsError);
  const userType = useSelector(selectors.userType);
  const isAdmin = userType === ADMIN;

  const [blueprintId, setBlueprintId] = useState<number | undefined>(undefined);
  const [variantId, setVariantId] = useState<number | undefined>(undefined);
  const [launchEngine, setLaunchEngine] = useState<'fast' | 'legacy'>('fast');

  useEffect(() => {
    setLaunchEngine('fast');
  }, [blueprintId, variantId]);

  useEffect(() => {
    if (visible) dispatch(listBlueprints({ summary_scope: 'none' }));
  }, [dispatch, visible]);

  // A scenario is launchable only if its latest published version has at least
  // one VISIBLE variant. If every difficulty is hidden there is nothing to
  // launch, so the scenario is not offered; engineering fixtures are dropped
  // for the same reason — a trainer cannot run a class on either.
  const launchable = useMemo(
    () =>
      blueprints.filter(b => {
        if (isEngineeringFixture(b)) return false;
        const v = latestPublishedVersion(b);
        return !!v && v.variants.some(variant => variant.visible_to_trainers);
      }),
    [blueprints],
  );

  const selected = launchable.find(b => b.id === blueprintId);
  const version = selected ? latestPublishedVersion(selected) : undefined;
  // Trainers may only launch variants explicitly made visible.
  const visibleVariants: BlueprintVariant[] = useMemo(
    () => (version?.variants ?? []).filter(v => v.visible_to_trainers),
    [version],
  );

  // Auto-pick the sole visible variant; clear when the family changes.
  useEffect(() => {
    if (visibleVariants.length === 1) {
      setVariantId(visibleVariants[0].id);
    } else {
      setVariantId(current =>
        visibleVariants.some(v => v.id === current) ? current : undefined,
      );
    }
  }, [blueprintId, visibleVariants]);

  const chosenVariant = visibleVariants.find(v => v.id === variantId);
  const canChooseEngine =
    (isAdmin || userType === TRAINER) &&
    chosenVariant?.launch_engines?.includes('fast') &&
    chosenVariant?.launch_engines?.includes('legacy');
  const preparation = useBlueprintPreparation({
    blueprintId: selected?.id,
    variantId: chosenVariant?.id,
    enabled: visible,
    revision: selected?.updated,
    refreshToken: selected,
  });
  const summary = preparation.summary;
  const preparing = preparation.preparing;
  // Dates and counts can be shown while checking, but stale key/coverage
  // warnings must wait for the chosen exercise's authoritative preparation.
  const description = summary || launchSummaryOf(chosenVariant, version);
  // No answer key means this sitting is not graded at all, which is a
  // different sentence from "nobody has validity-checked the answer key".
  // Withholding the document keeps the banner quiet rather than warning about
  // a key that does not exist.
  const gate = useMemo(
    () =>
      readAnswerKeyGate(
        summary?.answer_key
          ? { answer_key_gate: summary.answer_key_gate }
          : undefined,
      ),
    [summary],
  );

  // Coverage diagnostics are for administrators only.
  const coverage = useMemo(
    () => (isAdmin && summary?.answer_key ? readCoverage(summary) : undefined),
    [isAdmin, summary],
  );

  // The endpoint answers this for the same variant with the same sentence, so
  // a trainer never has to press Launch to find out that it is refused.
  const refused = summary?.launchable === false;
  // An admin can take an answer key out of service without deleting it.
  //
  // THIS USED TO READ `gate.launchBlocked` ALONE, AND THE GATE IS ADMIN-ONLY.
  // A trainer -- and by permission design a client observer -- is served no
  // gate at all, so the withdrawal reached nobody who could act on it and the
  // modal drew an enabled Launch button for a scenario the product had
  // withdrawn. The withdrawal is a fact about the CARD now: `launchable` goes
  // false for every reader, and the redacted note names the door. The gate is
  // kept only as the ADMIN's spelling of the same fact, so the person who
  // withdrew it still reads their own reason below.
  const outOfService =
    refused &&
    (Boolean(gate?.launchBlocked) ||
      (summary?.launch_refusal_note ?? '').includes('taken out of service'));
  const canLaunch =
    !!selected &&
    !!version &&
    !!variantId &&
    visibleVariants.length > 0 &&
    !!summary &&
    !preparing &&
    !preparation.error &&
    // A card whose gate says withdrawn while `launchable` still says yes is a
    // card from before the endpoint asked the question. Refuse it either way:
    // a launch that the server is about to reject is not worth offering.
    !gate?.launchBlocked &&
    summary?.launchable === true;

  const reset = () => {
    setBlueprintId(undefined);
    setVariantId(undefined);
    setLaunchEngine('fast');
  };

  const unknown = (
    <Typography.Text type="secondary">Not recorded</Typography.Text>
  );

  return (
    <Modal
      title="Launch from Scenario"
      open={visible}
      onCancel={() => {
        reset();
        hide();
      }}
      footer={null}
      width={620}
    >
      {catalogueError ? (
        <LoadFailure
          subject="Scenarios"
          onRetry={() => dispatch(listBlueprints({ summary_scope: 'none' }))}
        />
      ) : waiting && launchable.length === 0 ? (
        <Spin aria-label="Loading scenarios" />
      ) : launchable.length === 0 ? (
        <Empty description="No scenario is ready to launch yet. A scenario appears here once one of its versions is published and at least one difficulty has been made visible to trainers." />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Typography.Text strong>Scenario</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              placeholder="Select a scenario"
              loading={waiting}
              value={blueprintId}
              onChange={value => setBlueprintId(value as number)}
              showSearch
              optionFilterProp="children"
            >
              {launchable.map(b => (
                <Select.Option key={b.id} value={b.id}>
                  {b.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          {selected && visibleVariants.length > 1 && (
            <div>
              <Typography.Text strong>Difficulty</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Select difficulty"
                value={variantId}
                onChange={value => setVariantId(value as number)}
              >
                {visibleVariants.map(v => (
                  <Select.Option key={v.id} value={v.id}>
                    {v.difficulty.label} (level {v.difficulty.level})
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}

          {canChooseEngine && (
            <div>
              <Typography.Text strong>Execution engine</Typography.Text>
              <Select
                aria-label="Execution engine"
                style={{ width: '100%', marginTop: 6 }}
                value={launchEngine}
                onChange={setLaunchEngine}
                options={[
                  { value: 'fast', label: 'Fast' },
                  { value: 'legacy', label: 'Legacy' },
                ]}
              />
              <Typography.Text type="secondary">
                Creates an independent session from this blueprint.
              </Typography.Text>
            </div>
          )}

          {selected && (
            <div>
              <Typography.Text strong>What this will run</Typography.Text>
              <Descriptions
                size="small"
                bordered
                column={2}
                style={{ marginTop: 6 }}
              >
                <Descriptions.Item label="Window">
                  {windowLabel(description) || unknown}
                </Descriptions.Item>
                <Descriptions.Item label="Periods">
                  {description?.period_count ?? unknown}
                </Descriptions.Item>
                {/* A count of instruments said nothing about whether any
                    of them were marked. Where the card carries the coverage
                    it replaces the count; where it does not (an older
                    deployment) the count is still better than nothing. */}
                {coverage ? (
                  <Descriptions.Item label="Graded">
                    <Space direction="vertical" size={0}>
                      <span>{gradedFraction(coverage)}</span>
                      {notGradedLine(coverage) && (
                        <Typography.Text type="secondary">
                          {notGradedLine(coverage)}
                        </Typography.Text>
                      )}
                    </Space>
                  </Descriptions.Item>
                ) : (
                  <Descriptions.Item label="Instruments">
                    {description?.instrument_count ?? unknown}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Difficulty">
                  {chosenVariant
                    ? `${chosenVariant.difficulty.label} (level ${chosenVariant.difficulty.level})`
                    : unknown}
                </Descriptions.Item>
                {isAdmin && (
                  <Descriptions.Item label="Answer key" span={2}>
                    <Space wrap size={[6, 6]}>
                      <span>
                        {preparing
                          ? 'Preparing assessment…'
                          : preparation.error
                            ? 'Could not check assessment'
                            : summary?.answer_key ||
                              (chosenVariant
                                ? 'None — this session is not graded'
                                : 'Select a difficulty')}
                      </span>
                      {/* The code is what the API and the logs call it; the name
                        is what it is called in a conversation. */}
                      {summary?.answer_key && summary?.answer_key_name ? (
                        <Typography.Text type="secondary">
                          {summary.answer_key_name}
                        </Typography.Text>
                      ) : null}
                      {summary?.answer_key ? (
                        <AnswerKeyGateTag gate={gate} />
                      ) : null}
                    </Space>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Version" span={2}>
                  {version ? `v${version.version_number}` : unknown}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}

          {preparing && (
            <Alert
              type="info"
              showIcon
              title={isAdmin ? 'Preparing assessment' : 'Preparing scenario'}
              description={
                isAdmin
                  ? 'We are checking this version and updating its answer key where needed. This will refresh automatically when it is ready.'
                  : 'Checking the selected version. This will refresh automatically when it is ready.'
              }
            />
          )}

          {preparation.error && (
            <Alert
              type="error"
              showIcon
              title={
                isAdmin
                  ? 'Assessment could not be checked'
                  : 'Scenario could not be checked'
              }
              description={preparation.error}
              action={<Button onClick={preparation.retry}>Retry</Button>}
            />
          )}

          {isAdmin && preparation.failed && !refused && (
            <Alert
              type="error"
              showIcon
              title="Assessment needs attention"
              description={
                preparation.regeneration?.message ||
                'An administrator must review this assessment before launch.'
              }
            />
          )}

          {outOfService && (
            <Alert
              type="error"
              showIcon
              title="This scenario has been taken out of service"
              // The reason, the person and the date are the administrator's:
              // they arrive on the gate, which only an admin is served. A
              // trainer reads the door and who to ask, and nothing else.
              description={
                isAdmin
                  ? gate?.launchBlockReason ||
                    'An administrator can say why on the blueprint page.'
                  : 'Please contact an administrator to make this scenario available.'
              }
            />
          )}

          {coverage && coverage.complete === false && (
            <Alert
              type="warning"
              showIcon
              title={INCOMPLETE_COVERAGE_HEADLINE}
              // The instruments themselves are named once, in the Graded row
              // above. What belongs here is the consequence, which is the
              // part a trainer cannot work out for themselves.
              description="A trainee who expresses their view through one of the instruments it does not mark will be scored as though they said nothing."
            />
          )}

          {refused && !outOfService && (
            <Alert
              type="error"
              showIcon
              title="This scenario cannot be launched yet"
              description={
                isAdmin
                  ? summary?.launch_refusal ||
                    summary?.launch_refusal_note ||
                    'The launch endpoint refuses this scenario. An administrator can say why on the blueprint page.'
                  : 'This scenario is currently unavailable. Please try again or contact an administrator.'
              }
            />
          )}

          {isAdmin && <VerdictBanner gate={gate} />}

          <div style={{ textAlign: 'right' }}>
            <Button
              onClick={() => {
                reset();
                hide();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={waiting || preparing}
              disabled={!canLaunch}
              onClick={() => {
                if (!selected || !variantId) return;
                dispatch(
                  launchBlueprint({
                    id: selected.id,
                    variant_id: variantId,
                    klass_id: klassId,
                    ...(canChooseEngine ? { execution_engine: launchEngine } : {}),
                  }),
                );
                reset();
                hide();
              }}
            >
              Launch
            </Button>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default LaunchScenarioModal;
